/**
 * Membership-based startup resolution (onboarding Phase 0, Task 0.3).
 *
 * Ownership is moving from `startup_profiles.user_id` (legacy single-owner) to
 * `team_members` (owner vs member) per docs/superpowers/specs/2026-07-09-
 * startup-member-onboarding-design.md §5. `resolveMyStartup` is the pure seam:
 * it resolves the authed caller's startup via their ACTIVE `team_members` row
 * → `teams.startup_id` first, and only falls back to the legacy
 * `startup_profiles.user_id` lookup when no membership row exists yet
 * (migration compatibility — startups created before this schema landed).
 *
 * A legacy `startup_profiles.user_id` match is treated as `role: 'owner'` /
 * `isOwner: true`: per spec §5, that column WAS the single-owner model this
 * replaces, so the sole legacy owner is the equivalent of a `team_members`
 * owner today.
 *
 * `isInvitee` is PROVENANCE-based (`team_members.invited_by != null`), never
 * derived from `!isOwner`. Create-path founders/members are enrolled with
 * `role: 'member'` via onboarding-join's `asCreator` path (`invited_by` left
 * null), so `!isOwner` misclassifies them as invitees. `invited_by` is set
 * only by `team-accept-invite` when a real invite is accepted; a legacy
 * `startup_profiles.user_id` owner has no `team_members` row at all, so it
 * resolves `isInvitee: false` unconditionally.
 *
 * `useMyStartup` wraps the seam in a React Query `useQuery` (object API, v5 —
 * no `onError`, so `handleError` is called inline in the query fn) and adapts
 * `null` on failure via caller-facing `isLoading` / `error` fields.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { handleError } from '@/lib/errors';
import type { Database } from '@/lib/supabase/database.types';

export interface MyStartupResolution {
  startupId: string | null;
  role: string | null;
  isOwner: boolean;
  /** Provenance-based, NOT `!isOwner` — see module doc + resolveMyStartup. */
  isInvitee: boolean;
}

const NO_STARTUP: MyStartupResolution = { startupId: null, role: null, isOwner: false, isInvitee: false };

interface TeamMembershipRow {
  role: string;
  invited_by: string | null;
  teams: { startup_id: string | null } | null;
}

export const myStartupKeys = {
  mine: () => ['startup', 'mine'] as const,
};

/**
 * Pure seam: resolves the caller's startup from an injected Supabase client.
 * No React, no singleton import — a mock client can be passed directly.
 */
export async function resolveMyStartup(
  client: SupabaseClient<Database>,
): Promise<MyStartupResolution> {
  const { data: userData } = await client.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) return NO_STARTUP;

  // Nested-embed selects (`teams:team_id(startup_id)`) don't type-check cleanly
  // against the generated `Database` union without a per-query embed overload;
  // cast at the query boundary the same way use-interview.ts does (`const db =
  // supabase as any`) rather than hand-rolling a parallel embed type.
  const db = client as any;

  const { data: membership, error: membershipError } = await db
    .from('team_members')
    .select('role, invited_by, teams:team_id(startup_id)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();
  if (membershipError) throw membershipError;

  const membershipRow = membership as TeamMembershipRow | null;
  if (membershipRow?.teams?.startup_id) {
    return {
      startupId: membershipRow.teams.startup_id,
      role: membershipRow.role,
      isOwner: membershipRow.role === 'owner',
      isInvitee: membershipRow.invited_by != null,
    };
  }

  // Membership is the single ownership model (2026-07-21): a user with no active
  // team_members row owns no startup. The legacy startup_profiles.user_id
  // fallback was removed — the backfill guarantees every prior user_id owner has
  // an active owner membership, so this loses no one.
  return NO_STARTUP;
}

/**
 * `useMyStartup` — React Query wrapper around `resolveMyStartup`. Errors are
 * reported via `handleError` (context 'useMyStartup') and rethrown so React
 * Query surfaces them on `error`; callers get `null`/`false` defaults while
 * loading or failed rather than treating "unknown" as "no startup".
 */
export function useMyStartup(): {
  startupId: string | null;
  role: string | null;
  isOwner: boolean;
  isInvitee: boolean;
  isLoading: boolean;
  error: unknown;
} {
  const query = useQuery({
    queryKey: myStartupKeys.mine(),
    queryFn: async (): Promise<MyStartupResolution> => {
      try {
        return await resolveMyStartup(supabase);
      } catch (err) {
        handleError(err, { context: 'useMyStartup' });
        throw err;
      }
    },
  });

  return {
    startupId: query.data?.startupId ?? null,
    role: query.data?.role ?? null,
    isOwner: query.data?.isOwner ?? false,
    isInvitee: query.data?.isInvitee ?? false,
    isLoading: query.isLoading,
    error: query.error,
  };
}
